import styled from '@xstyled/styled-components';
import React, { useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import { useNextSequence } from '~/training/useNextSequence';
import { NextButton, PlayButton } from '~/ui/buttons';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { SequenceStore, useSequenceStore } from './SequenceStore';
import { drillAnimation } from '~/ui/animation';
import { t } from 'ttag';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { useQueryParams } from '~/lib/useQuery';

interface Props {
  hideNext?: boolean;
}

const selector = (s: MediaPlayerStore) =>
  select(s, 'status', 'toggle', 'progress');

const sequenceSelector = (s: SequenceStore) =>
  select(s, 'blockers', 'autoContinue');

const DrillBoxWrapper = styled.div`
  width: 100%;
  display: flex;
  position: relative;
  padding: 8px 25px;
  margin-bottom: -8px;

  span {
    font-size: 14px;
    z-index: 1;
    opacity: 0.7;
    color: #ffffff;
  }

  @media (max-width: 768px) {
    padding: 6px 16px;
    margin-bottom: -6px;

    span {
      width: 120px;
      text-align: center;
    }

    .drill-box-animation {
      height: 60px;
      opacity: 0.7;
    }
  }
`;

const DrillBoxAnimation = styled.div`
  width: 100%;
  height: 40px;
  left: 0;
  top: 0;
  animation: ${drillAnimation} 2.3s ease infinite;
  position: absolute;
  background-color: accentDark;
  border-radius: 20px;
  opacity: 0.3;

  @media (max-height: 680px) and (max-width: 768px) {
    opacity: 1;
  }
`;

const ButtonWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 100;

  @media (max-width: 580px) and (max-height: 300px) {
    bottom: 5px;
    right: 10px;
  }
`;

const PleaseSelectOption = () => {
  const [playAnimation, setPlayAnimation] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setPlayAnimation(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {playAnimation && (
        <DrillBoxWrapper>
          <DrillBoxAnimation className="drill-box-animation" />

          <span>{t`Complete the steps to continue`}</span>
        </DrillBoxWrapper>
      )}
    </>
  );
};

export const ComboButton = ({ hideNext }: Props) => {
  const next = useNextSequence();
  const params = useQueryParams();
  const { status, toggle, progress } = useMediaPlayerStore(selector, shallow);
  const { blockers, autoContinue } = useSequenceStore(sequenceSelector);
  const [disabled, setDisabled] = useState(false);

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  if (status === 'loading' || (autoContinue && status === 'done')) {
    return null;
  }

  const goNext = () => {
    if (!disabled) {
      setDisabled(true);
      if (globalPlayer) {
        try {
          globalPlayer.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
      }
      setTimeout(() => setDisabled(false), 1000);
      next();
    }
  };

  return (
    <ButtonWrapper>
      {status === 'nomedia' || status === 'done' ? (
        blockers.length === 0 && !hideNext ? (
          params.get('removeAllButtons') !== 'true' && (
            <NextButton
              id="nextBtn"
              onClick={goNext}
              disabled={disabled}
              data-test-id="next-button"
            />
          )
        ) : (
          (hideNext || hideNext == undefined) && <PleaseSelectOption />
        )
      ) : (
        <PlayButton state={status} onClick={toggle} progress={progress} />
      )}
    </ButtonWrapper>
  );
};
